import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  clearAuthSession,
  getAuthToken,
  getCurrentUser,
  fetchOauthMe,
  login as loginApi,
  register as registerApi,
  setAuthSession,
  addEmailToAccount as addEmailToAccountApi,
  changePassword as changeAccountPasswordApi,
} from "../services/authApi";
import {
  requestWalletLoginNonce,
  verifyWalletLogin,
  requestWalletLinkNonce,
  linkWalletToAccount,
} from "../services/walletAuthApi";
import {
  trackLoginCompleted,
  trackRegistrationCompleted,
  trackWalletConnectionStarted,
  trackWalletConnected,
  trackWalletConnectionFailed,
  endSession,
} from "../services/analyticsClient";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  /** Hydrate from localStorage immediately so `isAuthenticated` matches Bearer-backed API calls (e.g. Stripe checkout). */
  const [user, setUser] = useState(() =>
    typeof window !== "undefined" ? getCurrentUser() : null
  );
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? getAuthToken() || "" : ""
  );
  const [loading, setLoading] = useState(() =>
    typeof window !== "undefined" ? Boolean(getAuthToken()) : false
  );

  const refreshFromStorage = () => {
    try {
      const nextToken = getAuthToken();
      const nextUser = getCurrentUser();
      setToken(nextToken || "");
      setUser(nextUser || null);
    } catch {
      setToken("");
      setUser(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const t = getAuthToken();
      if (!t) {
        setToken("");
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        await fetchOauthMe();
        if (cancelled) return;
        setToken(getAuthToken());
        setUser(getCurrentUser());
      } catch {
        if (cancelled) return;
        clearAuthSession();
        setToken("");
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    const response = await loginApi(email, password);
    setToken(response.token || "");
    setUser(response.user || null);
    trackLoginCompleted({ method: "email" });
    return response;
  };

  const register = async (payload) => {
    const response = await registerApi(payload);
    setToken(response.token || "");
    setUser(response.user || null);
    trackRegistrationCompleted({ method: "email" });
    trackLoginCompleted({ method: "email" });
    return response;
  };

  const logout = () => {
    endSession({ area: "account" }).catch(() => {});
    clearAuthSession();
    setToken("");
    setUser(null);
  };

  const updateCurrentUser = useCallback((nextUser) => {
    const t = getAuthToken();
    if (t && nextUser) {
      setAuthSession(t, nextUser);
    }
    setUser(nextUser || null);
  }, []);

  /**
   * Wallet-only sign-in: caller supplies wagmi `signMessageAsync` after the wallet is connected.
   */
  const walletLogin = useCallback(async ({ walletAddress, signMessageAsync }) => {
    trackWalletConnectionStarted({ area: "wallet" });
    try {
      const { message } = await requestWalletLoginNonce(walletAddress);
      const signature = await signMessageAsync({ message });
      const session = await verifyWalletLogin(walletAddress, signature);
      setAuthSession(session.token, session.user);
      setToken(session.token || "");
      setUser(session.user || null);
      trackWalletConnected({ area: "wallet" });
      trackLoginCompleted({ method: "wallet" });
      return session;
    } catch (err) {
      trackWalletConnectionFailed({ area: "wallet", reason: String(err?.message || "failed").slice(0, 120) });
      throw err;
    }
  }, []);

  /**
   * Link wallet for an email (or hybrid) session; requires Bearer token.
   */
  const linkWallet = useCallback(
    async ({ walletAddress, signMessageAsync }) => {
      const noncePayload = await requestWalletLinkNonce(walletAddress);
      if (noncePayload.alreadyLinked && noncePayload.user) {
        updateCurrentUser(noncePayload.user);
      } else {
        const signature = await signMessageAsync({ message: noncePayload.message });
        const { user: nextUser } = await linkWalletToAccount(walletAddress, signature);
        updateCurrentUser(nextUser);
      }
      try {
        await fetchOauthMe();
      } catch {
        /* keep cached user if /me fails */
      }
      const synced = getCurrentUser();
      setUser(synced || null);
      return { user: synced };
    },
    [updateCurrentUser]
  );

  const addEmailToAccount = useCallback(async (payload) => {
    const data = await addEmailToAccountApi(payload);
    setUser(data.user || getCurrentUser());
    return data;
  }, []);

  const changePassword = useCallback(async (payload) => {
    const data = await changeAccountPasswordApi(payload);
    if (data?.user) setUser(data.user);
    return data;
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token),
      loading,
      login,
      register,
      logout,
      refreshFromStorage,
      walletLogin,
      linkWallet,
      updateCurrentUser,
      addEmailToAccount,
      changePassword,
    }),
    [user, token, loading, walletLogin, linkWallet, updateCurrentUser, addEmailToAccount, changePassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }
  return ctx;
};
